// Session state: who's signed in, what they're entitled to, and their favourites.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import type { Me } from "./types";
import { useToast } from "./toast";

interface SessionValue {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  favorites: Set<string>;
  toggleFavorite: (lessonId: string) => Promise<boolean>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const toast = useToast();

  const refresh = useCallback(async () => {
    try {
      const m = await api.me();
      setMe(m);
      if (m.user) {
        const f = await api.favorites();
        setFavorites(new Set(f.favorites.map((x) => x.lessonId)));
      } else setFavorites(new Set());
    } catch {
      // API unreachable (e.g. `vite` without `netlify dev`): browsing still works offline.
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggleFavorite = useCallback(
    async (lessonId: string) => {
      if (!me?.user) return false;
      const has = favorites.has(lessonId);
      const next = new Set(favorites);
      if (has) next.delete(lessonId);
      else next.add(lessonId);
      setFavorites(next);
      try {
        if (has) await api.removeFavorite(lessonId);
        else await api.addFavorite(lessonId);
        toast(has ? `Removed ${lessonId} from saved lessons` : `Saved ${lessonId}`);
      } catch (e) {
        setFavorites(favorites);
        toast((e as Error).message, "error");
      }
      return true;
    },
    [favorites, me, toast],
  );

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    await refresh();
    toast("Signed out");
  }, [refresh, toast]);

  const value = useMemo(() => ({ me, loading, refresh, favorites, toggleFavorite, signOut }), [me, loading, refresh, favorites, toggleFavorite, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside provider");
  return v;
}
